/**
 * Regional Analysis Translations Module (English)
 * 
 * Contains: Regional Analysis, Regional Distribution, Regional YFI Analysis, Regional Maps
 * 
 * @module regional
 * @language en
 */

export const regional = {
  regional: {
    title: "Regional Analysis",
    subtitle: "Active members by geographic region",
    asiaPacific: "Asia and the Pacific",
    europe: "Europe",
    americas: "America",
    africaMiddleEast: "Africa and the Middle East"
  },
  regionalAnalysisPage: {
    pageSubtitle: "1,000+ projects • 148,122 members • 4,641 Local Organizations across 4 JCI regions"
  },
  regionalDistribution: {
    title: "Regional Distribution",
    subtitle: "1,000+ projects across 4 JCI regions in 2024-2025",
    totalMembers: "Total Members",
    localOrganizations: "Local Organizations",
    countries: "Countries",
    annualProjects: "Annual Projects",
    projectDistributionTitle: "Project Distribution by Region (2024-2025)",
    projects: "projects",
    risePillarTitle: "JCI RISE Pillars Distribution",
    total: "Total",
    mentalHealth: "Mental Health",
    mentalHealthBeneficiaries: "5.9M+ beneficiaries reached",
    workforceResilience: "Workforce Resilience",
    workforceDesc: "Skills and employment support",
    economicRecovery: "Economic Recovery",
    economicDesc: "Entrepreneurship and economic growth",
    totalBeneficiaries: "Total beneficiaries reached through 1000+ JCI projects",
    membershipDistributionTitle: "Membership Distribution by Region",
    fiiScore: "YFI Score",
    members: "Members",
    localOrgs: "Local Orgs",
    projectShare: "Project Share",
    fastest: "Fastest",
    emerging: "Emerging",
    stable: "Stable",
    consolidating: "Consolidating",
    fastestGrowing: "Fastest Growing",
    emergingPotential: "Emerging Potential",
    sourceNote: "Source: JCI Impact Report 2025 - Official regional data"
  },
  regionalFIIAnalysis: {
    title: "Regional YFI Analysis",
    regionalComparison: "Regional YFI Comparison",
    fiiScore: "YFI Score",
    gapBestWorst: "Gap between best and worst",
    gapIndicates: "This gap indicates significant global inequality in youth opportunity",
    bestPerforming: "BEST PERFORMING",
    mixedPerformance: "MIXED PERFORMANCE",
    equityFocusNeeded: "EQUITY FOCUS NEEDED",
    highestOpportunity: "HIGHEST OPPORTUNITY",
    countriesWithData: "Countries with data",
    mentalHealth: "Mental Health",
    workforce: "Workforce",
    entrepreneurship: "Entrepreneurship",
    strengths: "Strengths",
    challenges: "Challenges",
    recommendations: "Recommendations",
    jciOpportunity: "JCI Opportunity",
    members: "members",
    localOrgs: "local orgs"
  },
  regionalFII: {
    title: "Regional YFI Comparison",
    fiiScore: "YFI Score",
    gapIndicator: "Gap between best and worst",
    points: "points",
    gapDescription: "This gap indicates significant global inequality in youth opportunity",
    mentalHealth: "Mental Health",
    workforce: "Workforce",
    entrepreneurship: "Entrepreneurship",
    countriesWithData: "Countries with Data",
    strengths: "Strengths",
    challenges: "Challenges",
    recommendations: "Recommendations",
    jciOpportunity: "JCI Opportunity",
    members: "Members",
    localOrgs: "Local Orgs",
    regions: {
      europe: "Europe",
      asiaPacific: "Asia and the Pacific",
      americas: "America",
      africaMe: "Africa and the Middle East"
    },
    status: {
      bestPerforming: "Best Performing",
      mixedPerformance: "Mixed Performance",
      equityFocus: "Equity Focus Needed",
      highestOpportunity: "Highest Opportunity"
    },
    europe: {
      strength1: "Mental Health Service Access: 77/100",
      strength2: "Workforce quality: 76/100",
      strength3: "Suicide prevention: 73/100",
      strength4: "Policy support: Universal healthcare integration",
      challenge1: "Stigma reduction needed",
      challenge2: "Pockets of youth unemployment",
      challenge3: "Entrepreneurship support could be stronger",
      recommendation1: "Expand digital mental health access",
      recommendation2: "Increase early intervention",
      recommendation3: "Support women entrepreneurs",
      insight: "Europe leads → model for other regions",
      expansion: "Consider expansion to 50+ organizations"
    },
    asiaPacific: {
      strength1: "Economic growth: Strong business environment",
      strength2: "Entrepreneurship: Highest early-stage rates",
      strength3: "Tech adoption: Fastest digital transformation",
      strength4: "Unicorn ecosystem",
      challenge1: "High disparity: 40-point gap",
      challenge2: "Mental health access: Only 30-50%",
      challenge3: "Skills gap",
      challenge4: "Pockets of unemployment",
      recommendation1: "Prioritize mental health",
      recommendation2: "Close skills gap with JCI training",
      recommendation3: "Scale RISE projects",
      insight: "70% of global JCI = strong presence",
      expansion: "Southeast Asia is underserved"
    },
    americas: {
      strength1: "Entrepreneurship: 24-28% early-stage rates",
      strength2: "Education: 45%+ tertiary enrollment",
      strength3: "North America: Strong MH infrastructure",
      challenge1: "North-South gap: 34 points",
      challenge2: "Latin America: MH access 20-40%",
      challenge3: "Economic inequality",
      challenge4: "Gender gap",
      recommendation1: "Focus on Latin America RISE expansion",
      recommendation2: "Build women entrepreneur support",
      recommendation3: "Expand digital skills training",
      insight: "Only 8% of global JCI but 15% of world = GROWTH POTENTIAL",
      expansion: "Scale from 90 RISE projects → 150+"
    },
    africaMe: {
      strength1: "Youth population: Youngest region (avg 29 years)",
      strength2: "Entrepreneurship aspiration: 52% of youth want to start businesses",
      strength3: "Growth potential: 10-20% annual GDP growth (some regions)",
      strength4: "Digital leapfrogging: Mobile-first adoption",
      challenge1: "Mental health access: Only 10-30% (EU is 70%)",
      challenge2: "Healthcare infrastructure: Critical shortage",
      challenge3: "Youth unemployment: 14-16% (global is 13%)",
      challenge4: "$1.7T funding gap, 30% is Africa/ME",
      challenge5: "Gender gap: Unequal access to education and opportunities",
      challenge6: "Brain drain: Emigration of skilled youth",
      limitedData: "Limited: 18 of 54 African countries with complete data",
      recommendation1: "Implement peer support mental health model (non-clinical)",
      recommendation2: "Scale STEM + digital skills training via JCI",
      recommendation3: "Expand microfinance + mentorship for youth entrepreneurship",
      recommendation4: "Focus on gender empowerment programs",
      insight: "YFI Gap: 22 points vs Europe = addressable. Critical expansion region",
      expansion: "Scale JCI RISE projects 3x"
    }
  },
  regionalMap: {
    title: "Regional Member Distribution",
    subtitle: "Click on a region to explore details",
    resetView: "Reset View",
    members: "Members",
    localOrgs: "Local Orgs",
    growth: "Growth",
    regions: {
      asiaPacific: "Asia and the Pacific",
      europe: "Europe",
      africaMe: "Africa and the Middle East",
      americas: "America"
    },
    growthStatus: {
      fastest: "Fastest Growing",
      stable: "Stable",
      emerging: "Emerging Potential",
      consolidating: "Consolidating"
    }
  }
} as const;

